import { array, coerce, discriminatedUnion, literal, number, object, preprocess } from 'zod';
import {
  IS_INTEGER,
  IS_POSITIVE,
  IS_REQUIRED,
  isRangeBetween,
} from '@occc-app/lib/constants/validationMessages';
import { Max32bitIntSchema, ProductGroupsSchema } from '@occc/validation';

import { BasePromotionSchema } from './base-promotion.schema';

import { ProductDiscountAwardType, VarietyDiscountAwardType, PromotionType } from '../types';

const VarietyPurchaseRowSchema = object({
  purchaseThreshold: number({ required_error: IS_REQUIRED })
    .gt(0, { message: IS_POSITIVE })
    .int({ message: IS_INTEGER }),
  eligibleProductGroups: ProductGroupsSchema,
});

const VarietyPurchasesSchema = array(VarietyPurchaseRowSchema).optional();

export const VarietyDiscountEarningMechanismSchema = discriminatedUnion('awardType', [
  object({
    awardType: literal(VarietyDiscountAwardType.TransactionDiscount),
    purchases: VarietyPurchasesSchema,
    transactionDiscount: preprocess(
      (val) => val ?? {},
      object({
        moneyDiscount: number({ required_error: IS_REQUIRED }).gt(0, { message: IS_POSITIVE }),
      }),
    ),
  }),
  object({
    awardType: literal(VarietyDiscountAwardType.ProductDiscount),
    purchases: VarietyPurchasesSchema,
    productDiscount: preprocess(
      (val) => val ?? {},
      discriminatedUnion('awardType', [
        object({
          awardType: literal(ProductDiscountAwardType.Free),
          eligibleProductGroups: ProductGroupsSchema,
          purchaseThreshold: number({ required_error: IS_REQUIRED })
            .gt(0, { message: IS_POSITIVE })
            .int({ message: IS_INTEGER }),
        }),
        object({
          awardType: literal(ProductDiscountAwardType.Discount),
          eligibleProductGroups: ProductGroupsSchema,
          purchaseThreshold: number({ required_error: IS_REQUIRED })
            .gt(0, { message: IS_POSITIVE })
            .int({ message: IS_INTEGER }),
          percentDiscount: coerce
            .number({ message: IS_REQUIRED })
            .int({ message: IS_INTEGER })
            .min(0, { message: isRangeBetween(0, 100) })
            .max(100, { message: isRangeBetween(0, 100) }),
        }),
        object({
          awardType: literal(ProductDiscountAwardType.PriceOverride),
          eligibleProductGroups: ProductGroupsSchema,
          purchaseThreshold: number({ required_error: IS_REQUIRED })
            .gt(0, { message: IS_POSITIVE })
            .int({ message: IS_INTEGER }),
        }),
      ]),
    ),
  }),
  object({
    awardType: literal(VarietyDiscountAwardType.PriceOverride),
    purchases: VarietyPurchasesSchema,
    priceOverride: preprocess(
      (val) => val ?? {},
      object({
        maximumCost: Max32bitIntSchema(
          number({ required_error: IS_REQUIRED }).gt(0, { message: IS_POSITIVE }),
        ),
      }),
    ),
  }),
]);

export const VarietyDiscountFormSchema = BasePromotionSchema.extend({
  offerType: literal(PromotionType.VarietyDiscount),
  varietyDiscount: preprocess((val) => val ?? {}, VarietyDiscountEarningMechanismSchema),
});
